'use client';

import { useEffect, useRef } from 'react';

/**
 * Grid-locked glow field for /pricing: individual cells of the real page
 * grid light up. The canvas is fixed to the viewport (a document-sized
 * canvas would be far too heavy), but every cell is DOCUMENT-anchored —
 * drawing subtracts window.scrollY each frame — so the glows scroll with
 * the content, exactly like the body grid they sit on. Geometry comes from
 * the HeroGrid CSS vars (--grid-cell / --grid-offset-x), so every glowing
 * square sits exactly inside a painted grid square.
 *
 * Three behaviours, all snapped to cells:
 * - ambient: a sparse pool of cells slowly breathe in brand pink;
 * - ping: occasionally a cell outline draws crisply and fades;
 * - pointer: the cell under the cursor (plus its four neighbours) lights up
 *   and decays into a trail; clicking pulses a 3×3 block.
 *
 * The center white wash keeps the content column calm — it also fades the
 * body grid lines where text lives, which is why /pricing needs no
 * GridClearing machinery. Desktop only; under prefers-reduced-motion a
 * static, deterministic scattering of faint cells is drawn instead (redrawn
 * on scroll so it stays attached to the document — no animation).
 */

type Glow = {
  i: number;
  j: number;
  start: number;
  dur: number;
  peak: number;
  ring: boolean;
};

const FILL = '240, 96, 144'; // --color-brand-400
const RING = '215, 14, 98'; // --color-brand-600
const MAX_AMBIENT = 12;

export default function GridGlow() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = window.matchMedia('(min-width: 768px)');

    let raf = 0;
    let glows: Glow[] = [];
    const energy = new Map<string, number>(); // "i,j" → pointer-trail intensity
    let cell = 72;
    let offsetX = 0;
    let w = 0;
    let h = 0;
    let last = 0;
    let nextSpawn = 0;

    const readGrid = () => {
      const cs = getComputedStyle(document.documentElement);
      cell = parseFloat(cs.getPropertyValue('--grid-cell')) || 72;
      const raw = parseFloat(cs.getPropertyValue('--grid-offset-x'));
      const container = parseFloat(cs.getPropertyValue('--grid-container-width')) || 1224;
      offsetX = Number.isFinite(raw) ? raw : (window.innerWidth - container) / 2;
    };

    // Columns are screen-anchored (the grid never moves horizontally); rows
    // are document-anchored, so screen→row goes through scrollY and drawing
    // a row subtracts it again.
    const colAt = (x: number) => Math.floor((x - offsetX) / cell);
    const rowAt = (screenY: number) => Math.floor((screenY + window.scrollY) / cell);
    const cellRect = (i: number, j: number) =>
      [offsetX + i * cell, j * cell - window.scrollY] as const;

    /** Reduced-motion fallback: one deterministic sparse frame, no loop. */
    const drawStatic = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = `rgba(${FILL}, 0.05)`;
      for (let i = colAt(0); i <= colAt(w); i++) {
        for (let j = rowAt(0); j <= rowAt(h); j++) {
          // Simple integer hash → ~1 in 14 cells lit, stable across paints.
          const hash = ((i * 73856093) ^ (j * 19349663)) >>> 0;
          if (hash % 14 !== 0) continue;
          const [x, y] = cellRect(i, j);
          ctx.fillRect(x + 1, y + 1, cell - 1, cell - 1);
        }
      }
    };

    /** Keeps the static frame document-anchored while the user scrolls —
     *  active only in reduced-motion mode (the rAF loop handles it live). */
    const onStaticScroll = () => {
      if (reduced.matches && desktop.matches) drawStatic();
    };

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      readGrid();
      if (reduced.matches) drawStatic();
    };

    const spawn = (now: number) => {
      if (glows.length >= MAX_AMBIENT) return;
      const i = colAt(Math.random() * w);
      const j = rowAt(Math.random() * h);
      const ring = Math.random() < 0.16;
      glows.push({
        i,
        j,
        start: now,
        dur: ring ? 1300 + Math.random() * 500 : 3000 + Math.random() * 2400,
        peak: ring ? 0.3 + Math.random() * 0.15 : 0.05 + Math.random() * 0.09,
        ring,
      });
    };

    const bump = (i: number, j: number, amount: number) => {
      const key = `${i},${j}`;
      energy.set(key, Math.max(energy.get(key) ?? 0, amount));
    };

    const onPointerMove = (e: PointerEvent) => {
      if (reduced.matches || !desktop.matches) return;
      const i = colAt(e.clientX);
      const j = rowAt(e.clientY);
      bump(i, j, 1);
      bump(i - 1, j, 0.35);
      bump(i + 1, j, 0.35);
      bump(i, j - 1, 0.35);
      bump(i, j + 1, 0.35);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (reduced.matches || !desktop.matches) return;
      const i = colAt(e.clientX);
      const j = rowAt(e.clientY);
      for (let di = -1; di <= 1; di++)
        for (let dj = -1; dj <= 1; dj++)
          bump(i + di, j + dj, di === 0 && dj === 0 ? 1.6 : 0.8);
    };

    const draw = (now: number) => {
      const dt = last ? now - last : 16;
      last = now;
      ctx.clearRect(0, 0, w, h);

      if (now >= nextSpawn) {
        spawn(now);
        nextSpawn = now + 320 + Math.random() * 680;
      }

      glows = glows.filter((g) => now - g.start < g.dur);
      for (const g of glows) {
        const t = (now - g.start) / g.dur;
        const a = Math.sin(Math.PI * t) * g.peak;
        const [x, y] = cellRect(g.i, g.j);
        if (y < -cell || y > h) continue; // scrolled out of view
        if (g.ring) {
          ctx.strokeStyle = `rgba(${RING}, ${a})`;
          ctx.lineWidth = 1;
          ctx.strokeRect(x + 2, y + 2, cell - 3, cell - 3);
        } else {
          ctx.fillStyle = `rgba(${FILL}, ${a})`;
          ctx.fillRect(x + 1, y + 1, cell - 1, cell - 1);
        }
      }

      const decay = Math.pow(0.5, dt / 420);
      for (const [key, v] of energy) {
        const nv = v * decay;
        if (nv < 0.012) {
          energy.delete(key);
          continue;
        }
        energy.set(key, nv);
        const [i, j] = key.split(',').map(Number);
        const [x, y] = cellRect(i, j);
        if (y < -cell || y > h) continue;
        ctx.fillStyle = `rgba(${FILL}, ${Math.min(nv, 1.25) * 0.15})`;
        ctx.fillRect(x + 1, y + 1, cell - 1, cell - 1);
      }

      raf = requestAnimationFrame(draw);
    };

    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      last = 0;
    };

    const start = () => {
      if (raf || reduced.matches || !desktop.matches || document.hidden) return;
      raf = requestAnimationFrame(draw);
    };

    const onModeChange = () => {
      stop();
      glows = [];
      energy.clear();
      if (reduced.matches) drawStatic();
      else start();
    };

    const onVisibility = () => (document.hidden ? stop() : start());

    resize();
    start();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', onStaticScroll, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    reduced.addEventListener('change', onModeChange);
    desktop.addEventListener('change', onModeChange);

    return () => {
      stop();
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onStaticScroll);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('visibilitychange', onVisibility);
      reduced.removeEventListener('change', onModeChange);
      desktop.removeEventListener('change', onModeChange);
    };
  }, []);

  return (
    <div aria-hidden className="fixed inset-0 z-0 hidden md:block">
      {/* Explicit CSS size: a canvas is a replaced element, so inset-0 alone
          would keep it at its (dpr-scaled) attribute size instead of
          stretching it to the viewport. */}
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      {/* Whitish wash over the content column — fades the body grid and the
          glows where text lives; both stay full-strength toward the edges. */}
      <div className="pointer-events-none absolute inset-0 [background:linear-gradient(90deg,rgba(248,250,252,0)_4%,rgba(248,250,252,0.88)_32%,rgba(248,250,252,0.88)_68%,rgba(248,250,252,0)_96%)]" />
    </div>
  );
}
